import React, { type JSX,useEffect } from 'react';

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { fetchAclConditions } from 'common/services/api/Acls/AclConditions.thunk';

import { selectAclConditions } from './aclConditionsSelectors';
import {
    HISTORICAL_DAYS_BACK_CONDITION_KEY,
    HISTORICAL_DAYS_BACK_CONDITION_PARAMETER_NAME,
    HISTORICAL_START_DATE_CONDITION_KEY,
    HISTORICAL_START_DATE_CONDITION_PARAMETER_NAME,
    isValidDateString,
    isValidPositiveInteger
} from './aclConditionsValidation';

interface IAclConditionsCreateDropDownProps {
    conditions?: string[];
    currentConditionKey?: string;
    setCurrentConditionKey: (newKey: string) => void;
    currentConditionValue?: Record<string, Record<string, string>>;
    setCurrentConditionValue: (newValue: Record<string, Record<string, string>>) => void;
}

interface IAclConditionsEditDropDownProps {
    conditions?: Record<string, Record<string, string>>;
    onChange?: (updatedConditions: Record<string, Record<string, string>>) => void;
}

interface ICreateAclConditionValueInputProps {
    conditionKey: string;
    currentConditionValue?: Record<string, Record<string, string>>;
    setCurrentConditionValue: (newValue: Record<string, Record<string, string>>) => void;
}

const CreateAclConditionValueInput = ({ conditionKey, currentConditionValue, setCurrentConditionValue }: ICreateAclConditionValueInputProps): JSX.Element | null => {
    switch (conditionKey) {
        case HISTORICAL_START_DATE_CONDITION_KEY: {
            const currentDateValue = currentConditionValue?.[conditionKey]?.[HISTORICAL_START_DATE_CONDITION_PARAMETER_NAME] || '';
            const isDateValid = !currentDateValue || isValidDateString(currentDateValue);
            return <LocalizationProvider dateAdapter={AdapterDayjs}>
                <PalmDatePicker
                    label='Historical Start Date'
                    required
                    onChange={(value?: string) => {
                        const newVal: Record<string, Record<string, string>> = {
                            [conditionKey]: {
                                [HISTORICAL_START_DATE_CONDITION_PARAMETER_NAME]: value || ''
                            }
                        };
                        setCurrentConditionValue(newVal);
                    }}
                    value={currentDateValue}
                />
                {!isDateValid && <Typography component="span" sx={{ color: 'error.main', fontSize: '12px' }}>Invalid date format</Typography>}
            </LocalizationProvider>;
        }
        case HISTORICAL_DAYS_BACK_CONDITION_KEY: {
            const currentDaysValue = currentConditionValue?.[conditionKey]?.[HISTORICAL_DAYS_BACK_CONDITION_PARAMETER_NAME] || '';
            const isDaysValid = !currentDaysValue || isValidPositiveInteger(currentDaysValue);
            return <TextField
                type='number'
                id='acl-condition-historical-days-back-input'
                label='Historical Days Back'
                variant='standard'
                inputProps={{ min: 0, step: 1 }}
                required
                error={!isDaysValid}
                helperText={isDaysValid ? '' : 'Must be a positive integer (0 or greater)'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    const newVal: Record<string, Record<string, string>> = {
                        [conditionKey]: {
                            [HISTORICAL_DAYS_BACK_CONDITION_PARAMETER_NAME]: value || ''
                        }
                    };
                    setCurrentConditionValue(newVal);
                }}
                value={currentDaysValue}
            />;
        }
        default:
            return <TextField
                id='acl-condition-input'
                label='ACL Condition Value'
                variant='standard'
                required
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newVal: Record<string, Record<string, string>> = {
                        [conditionKey]: {
                            [conditionKey]: event.target.value || ''
                        }
                    };
                    setCurrentConditionValue(newVal);
                }}
                value={currentConditionValue?.[conditionKey]?.[conditionKey] || ''}
            />;
    }
};
interface IEditAclConditionValueInputProps {
    conditionKey: string;
    conditions: Record<string, Record<string, string>>;
    handleValueChange: (key: string, paramKey: string, value: string) => void;
}

const EditAclConditionValueInput = ({ conditionKey, conditions, handleValueChange }: IEditAclConditionValueInputProps): JSX.Element | null => {
    switch (conditionKey) {
        case HISTORICAL_START_DATE_CONDITION_KEY: {
            const currentDateValue = conditions[conditionKey]?.[HISTORICAL_START_DATE_CONDITION_PARAMETER_NAME] || '';
            const isDateValid = !currentDateValue || isValidDateString(currentDateValue);
            return <LocalizationProvider dateAdapter={AdapterDayjs}>
                <PalmDatePicker
                    label='Historical Start Date'
                    required
                    onChange={(value?: string) => {
                        handleValueChange(conditionKey, HISTORICAL_START_DATE_CONDITION_PARAMETER_NAME, value || '');
                    }}
                    value={currentDateValue}
                />
                {!isDateValid && <Typography component="span" sx={{ color: 'error.main', fontSize: '12px' }}>Invalid date format</Typography>}
            </LocalizationProvider>;
        }
        case HISTORICAL_DAYS_BACK_CONDITION_KEY: {
            const currentDaysValue = conditions[conditionKey]?.[HISTORICAL_DAYS_BACK_CONDITION_PARAMETER_NAME] || '';
            const isDaysValid = !currentDaysValue || isValidPositiveInteger(currentDaysValue);
            return <TextField
                type='number'
                id='acl-condition-historical-days-back-input'
                label='Historical Days Back'
                variant='standard'
                inputProps={{ min: 0, step: 1 }}
                required
                error={!isDaysValid}
                helperText={isDaysValid ? '' : 'Must be a positive integer (0 or greater)'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    handleValueChange(conditionKey, HISTORICAL_DAYS_BACK_CONDITION_PARAMETER_NAME, value);
                }}
                value={currentDaysValue}
            />;
        }
        default:
            return <TextField
                id='acl-condition-input'
                label='ACL Condition Value'
                variant='standard'
                required
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleValueChange(conditionKey, conditionKey, event.target.value);
                }}
                value={conditions[conditionKey]?.[conditionKey] || ''}
            />;
    }
};
export function AclConditionsCreateDropDown(props: Readonly<IAclConditionsCreateDropDownProps>) {
    const aclConditions = useAppSelector(selectAclConditions);
    const dispatch = useAppDispatch();
    const {
        conditions,
        currentConditionKey,
        setCurrentConditionKey,
        currentConditionValue,
        setCurrentConditionValue,
    } = props;

    useEffect(() => {
        if (aclConditions?.length) {
            return;
        }
        dispatch(fetchAclConditions({ key: '' })).catch(console.error);
    }, []);

    const handleCurrentConditionKeyChange = (evt: SelectChangeEvent) => {
        setCurrentConditionKey(evt.target.value);
    };

    const CreateAclConditionsDropDownElement = (conditions: string[]): JSX.Element[] => {
        const validAclConditions = aclConditions.filter(aclCondition =>
            conditions.includes(aclCondition.key)
        );

        return validAclConditions.map(condition => {
            return <MenuItem key={condition.key} value={condition.key}>{condition.name}</MenuItem>;
        });
    };

    return <FormControl>
        <InputLabel id="acl-condition-label">ACL Condition</InputLabel>
        {<Select
            labelId="acl-condition-label"
            onChange={handleCurrentConditionKeyChange}
            label="ACL Condition"
            value={currentConditionKey}
            sx={{ color: 'text.secondary', marginBottom: 1 }}>
            {conditions && CreateAclConditionsDropDownElement(conditions)}
        </Select>}
        {currentConditionKey && <CreateAclConditionValueInput
            conditionKey={currentConditionKey}
            currentConditionValue={currentConditionValue}
            setCurrentConditionValue={setCurrentConditionValue}
        />}
    </FormControl>;
}

export function AclConditionsEditDropDown(props: Readonly<IAclConditionsEditDropDownProps>) {
    const aclConditions = useAppSelector(selectAclConditions);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (aclConditions?.length) {
            return;
        }
        dispatch(fetchAclConditions({ key: '' })).catch(console.error);
    }, []);

    const { conditions, onChange } = props;
    const conditionKey = conditions ? Object.keys(conditions)[0] : '';
    const conditionName = aclConditions.find(c => c.key === conditionKey)?.name || '';

    const handleValueChange = (key: string, paramKey: string, value: string) => {
        if (!conditions || !onChange) return;
        const updated = {
            ...conditions,
            [key]: {
                ...conditions[key],
                [paramKey]: value
            }
        };
        onChange(updated);
    };

    return <FormControl>
        <TextField
            sx={{
                backgroundColor: 'background.paper',
                borderRadius: '4px',
                marginBottom: 1
            }}
            size="small"
            label="ACL Condition"
            variant="filled"
            value={conditionName}
            disabled
        />
        {conditions && conditionKey && <EditAclConditionValueInput
            conditionKey={conditionKey}
            conditions={conditions}
            handleValueChange={handleValueChange}
        />}
    </FormControl>;
}
